import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Database } from '@/integrations/supabase/types';

type Profile = Database['public']['Tables']['profiles']['Row'];

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  profile: Profile | null;
  checkUsernameAvailability: (username: string) => Promise<{ available: boolean; error?: string; }>;
  signUp: (email: string, password: string, username: string, fullName?: string) => Promise<{ error: any; success?: boolean }>;
  signIn: (email: string, password: string) => Promise<{ error: any; success?: boolean }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Fetch user profile data
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    console.log('[AUTH] 🔄 Setting up auth state listener...');

    // Track whether the listener has fired to prevent getSession() from overwriting it
    let listenerHasFired = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        listenerHasFired = true;
        console.log('[AUTH] 🔔 Auth state changed', {
          event,
          hasSession: !!session,
          userId: session?.user?.id,
          email: session?.user?.email
        });

        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        // Handle specific auth events
        if (event === 'SIGNED_IN') {
          console.log('[AUTH] ✅ User signed in successfully');
          toast({
            title: "Welcome back!",
            description: "You have successfully signed in.",
          });
        } else if (event === 'SIGNED_OUT') {
          console.log('[AUTH] 👋 User signed out');
          toast({
            title: "Signed out",
            description: "You have been successfully signed out.",
          });
        }
      }
    );

    // Fallback: if the listener hasn't fired yet (e.g. session already initialized
    // before the listener was registered), set state from getSession().
    // The guard prevents this from overwriting state the listener already set correctly,
    // which was the original race condition causing Google OAuth to appear to fail.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!listenerHasFired) {
        console.log('[AUTH] 📋 Fallback session check (listener had not fired)', {
          hasSession: !!session,
          userId: session?.user?.id,
        });
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    });

    return () => {
      console.log('[AUTH] 🧹 Cleaning up auth subscription');
      subscription.unsubscribe();
    };
  }, []);

  const checkUsernameAvailability = async (username: string): Promise<{ available: boolean; error?: string; }> => {
    const startTime = performance.now();
    try {
      console.log('[AUTH] checkUsernameAvailability started', { usernameLength: username.length });
      const { data, error } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', username)
        .maybeSingle();

      const duration = Math.round(performance.now() - startTime);
      if (error) {
        console.error('[AUTH] checkUsernameAvailability failed', { durationMs: duration, errorCode: error.code });
        return { available: false, error: 'Error checking username availability' };
      }

      const isAvailable = !data;
      console.log('[AUTH] checkUsernameAvailability completed', { durationMs: duration, isAvailable });
      return { available: isAvailable };
    } catch (error: any) {
      const duration = Math.round(performance.now() - startTime);
      console.error('[AUTH] checkUsernameAvailability network error', { durationMs: duration, errorMessage: error?.message });
      return { available: false, error: 'A network error occurred while checking username' };
    }
  };

  const signUp = async (email: string, password: string, username: string, fullName?: string) => {
    const startTime = performance.now();
    try {
      setLoading(true);
      console.log('[AUTH] signUp started', { usernameLength: username.length, hasFullName: !!fullName });
      
      // First check if username is available
      const usernameCheck = await checkUsernameAvailability(username);
      if (!usernameCheck.available) {
        const error = new Error(usernameCheck.error || 'Username already taken. Please choose another.');
        toast({
          title: "Username Issue",
          description: usernameCheck.error || "This username is already in use. Please choose a different one.",
          variant: "destructive",
        });
        return { error, success: false };
      }

      // Validate password strength
      if (password.length < 8) {
        const error = new Error('Password must be at least 8 characters long.');
        toast({
          title: "Password Too Short",
          description: "Password must be at least 8 characters long.",
          variant: "destructive",
        });
        return { error, success: false };
      }

      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            username: username,
            full_name: fullName,
          }
        }
      });

      const duration = Math.round(performance.now() - startTime);
      if (error) {
        console.error('[AUTH] signUp failed', { durationMs: duration, errorCode: error.code, errorMessage: error.message });
        toast({
          title: "Sign Up Error",
          description: error.message,
          variant: "destructive",
        });
        return { error, success: false };
      } else {
        console.log('[AUTH] signUp completed successfully', { durationMs: duration });
        toast({
          title: "Account Created!",
          description: "Please check your email to verify your account before signing in.",
        });
        return { error: null, success: true };
      }
    } catch (error: any) {
      const duration = Math.round(performance.now() - startTime);
      console.error('[AUTH] signUp exception', { durationMs: duration, errorMessage: error?.message });
      toast({
        title: "Sign Up Error",
        description: error.message || "An unexpected error occurred during sign up.",
        variant: "destructive",
      });
      return { error, success: false };
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const startTime = performance.now();
    try {
      setLoading(true);
      console.log('[AUTH] signIn started');
      
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      const duration = Math.round(performance.now() - startTime);
      if (error) {
        console.error('[AUTH] signIn failed', { durationMs: duration, errorCode: error.code, errorMessage: error.message });
        
        // Provide more specific error messages
        let errorMessage = error.message;
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = "Invalid email or password. Please check your credentials and try again.";
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = "Please verify your email address before signing in.";
        }
        
        toast({
          title: "Sign In Error",
          description: errorMessage,
          variant: "destructive",
        });
        return { error, success: false };
      }

      console.log('[AUTH] signIn completed successfully', { durationMs: duration });
      return { error: null, success: true };
    } catch (error: any) {
      const duration = Math.round(performance.now() - startTime);
      console.error('[AUTH] signIn exception', { durationMs: duration, errorMessage: error?.message });
      toast({
        title: "Sign In Error",
        description: error.message || "An unexpected error occurred during sign in.",
        variant: "destructive",
      });
      return { error, success: false };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    const startTime = performance.now();
    try {
      setLoading(true);
      console.log('[AUTH] signOut started');
      
      const { error } = await supabase.auth.signOut();
      const duration = Math.round(performance.now() - startTime);
      if (error) {
        console.error('[AUTH] signOut failed', { durationMs: duration, errorCode: error.code, errorMessage: error.message });
        toast({
          title: "Sign Out Error",
          description: error.message,
          variant: "destructive",
        });
      } else {
        console.log('[AUTH] signOut completed successfully', { durationMs: duration });
        // Navigation will be handled by the component using this hook
      }
    } catch (error: any) {
      const duration = Math.round(performance.now() - startTime);
      console.error('[AUTH] signOut exception', { durationMs: duration, errorMessage: error?.message });
      toast({
        title: "Sign Out Error",
        description: error.message || "An unexpected error occurred during sign out.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    session,
    loading,
    profile: profile || null,
    checkUsernameAvailability,
    signUp,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
